import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';

const float = keyframes`
  0%, 100% {
    transform: translateY(0) rotate(-10deg);
  }
  50% {
    transform: translateY(-10px) rotate(-10deg);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.5;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 0.5;
  }
`;

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const LogoWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1),
  position: 'relative',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-20%',
    left: '-20%',
    right: '-20%',
    bottom: '-20%',
    background: 'radial-gradient(circle, rgba(255,107,107,0.2) 0%, rgba(255,142,83,0) 70%)',
    borderRadius: '50%',
    animation: `${pulse} 3s ease-in-out infinite`,
    zIndex: -1,
  },
  '&:hover': {
    transform: 'scale(1.05)',
    '& .cart-icon': {
      animation: `${rotate} 1s ease-in-out`,
    },
    '& .logo-text': {
      backgroundSize: '200% 200%',
    },
  }
}));

const IconWrapper = styled(Box)(({ theme, size }) => ({
  background: 'linear-gradient(45deg, #FF6B6B, #FF8E53)',
  borderRadius: '15px',
  padding: size === 'large' ? theme.spacing(2) : size === 'medium' ? theme.spacing(1.5) : theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 4px 20px rgba(255, 107, 107, 0.3)',
  animation: `${float} 3s ease-in-out infinite`,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(45deg, #FF6B6B, #FF8E53)',
    borderRadius: 'inherit',
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  '&:hover::before': {
    opacity: 0.3,
  }
}));

const LogoText = styled(Typography)(({ theme, size }) => ({
  fontWeight: 800,
  letterSpacing: '0.5px',
  background: 'linear-gradient(45deg, #FF6B6B, #FF8E53, #FF6B6B)',
  backgroundSize: '100% 100%',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  transition: 'all 0.3s ease',
  fontSize: size === 'large' ? '2.5rem' : size === 'medium' ? '2rem' : '1.5rem',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-4px',
    left: 0,
    width: '100%',
    height: '2px',
    background: 'linear-gradient(90deg, #FF6B6B, #FF8E53)',
    transform: 'scaleX(0)',
    transformOrigin: 'left',
    transition: 'transform 0.3s ease',
  },
  '&:hover::after': {
    transform: 'scaleX(1)',
  }
}));

const Logo = ({ size = 'medium' }) => {
  const getIconSize = (size) => {
    switch (size) {
      case 'large':
        return 40;
      case 'medium':
        return 32;
      case 'small':
        return 24;
      default:
        return 32;
    }
  };

  return (
    <LogoWrapper>
      <IconWrapper size={size}>
        <ShoppingCartIcon 
          className="cart-icon"
          sx={{ 
            fontSize: getIconSize(size),
            color: 'white',
            transition: 'transform 0.3s ease',
          }} 
        />
      </IconWrapper>
      <LogoText 
        className="logo-text"
        size={size}
      >
        AkdmeShop
      </LogoText>
    </LogoWrapper>
  );
};

export default Logo;
